import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AlertTriangle, BookOpen, CalendarDays, Check, Cpu, ListChecks, NotebookPen, RotateCcw, Send, Settings2 } from 'lucide-react';
import { Link } from 'react-router';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { applyAll } from '../assistant/apply';
import { recordWrites, type WriteTrace } from '../db/repo';
import { AgendaCard, HabitsCard, TasksCard } from './ChatCards';
import { DictationButton } from '../ui/Dictation';
import { fetchAiStatus, hybridUnderstander, type AiStatus } from '../assistant/remote';
import { revertWrites } from '../domain/revert';
import {
  newConversation,
  pendingProposal,
  recordResult,
  recordPrefs,
  recordUndo,
  recoverConversation,
  step,
  withDailyBrief,
  type ConversationEnv,
  type ConversationState,
  type Effect,
  type Input,
  type ProposalRecord,
} from '../assistant/conversation';
import { describeProposal, plainI18n } from '../assistant/describe';
import { DESTRUCTIVE } from '../assistant/interpreter';
import { tr, type Message, type Part, type TextRef } from '../assistant/parts';
import { clearConversation, conversationOwner, loadConversation, loadPreferences, saveConversation, savePreferences } from '../assistant/store';
import { applyPreference, type Preferences } from '@habitos/assistant/assistant/preferences';
import { getMeta, setMeta } from '../db/db';
import { currentPlan, emptyDraft, PlanLimitError } from '../domain/actions';
import type { PlanId } from '@habitos/protocol';
import { uuidv7 } from '../lib/ids';
import { useSyncState } from '../sync/store';
import { useToast } from '../ui/Toast';

const EXAMPLES = ['chat.examples.routine', 'chat.examples.goal', 'chat.examples.neglected', 'chat.examples.agenda', 'assistant.examples.reminder', 'assistant.examples.log'] as const;

/**
 * Deshacer: lo normal es revertir la huella de escrituras guardada en la conversación (sobrevive
 * a recargar). Este registro en memoria cubre lo que no deja huella en la base de datos (por
 * ejemplo, iniciar una sesión de enfoque) mientras la app sigue abierta.
 */
const undoRegistry = new Map<string, () => Promise<void>>();

/** Reglas primero; la IA solo cuando las reglas no están seguras (ver assistant/remote.ts). */
const understander = hybridUnderstander();

/** ¿Hay una suscripción push activa en este dispositivo? (para no prometer avisos). */
async function hasPushSubscription(): Promise<boolean> {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    const reg = await navigator.serviceWorker?.getRegistration();
    return Boolean(await reg?.pushManager?.getSubscription());
  } catch {
    return false;
  }
}

/** Accesos directos a las secciones que el chat alimenta (agenda, listas, notas, diario). */
const SHORTCUTS = [
  { key: 'chat.shortcuts.agenda', Icon: CalendarDays, to: '/?view=agenda' },
  { key: 'chat.shortcuts.lists', Icon: ListChecks, to: '/brain?tab=lists' },
  { key: 'chat.shortcuts.notes', Icon: NotebookPen, to: '/brain?tab=notes' },
  { key: 'chat.shortcuts.journal', Icon: BookOpen, to: '/brain?tab=journal' },
] as const;

/** IA en uso: solo si el servidor la ofrece de verdad y el usuario no la desactivó. */
function useAi(): { status: AiStatus | null; active: boolean } {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    let alive = true;
    void getMeta<boolean>('assistantUseAi', true).then((v) => alive && setEnabled(v));
    void fetchAiStatus().then((s) => alive && setStatus(s));
    return () => {
      alive = false;
    };
  }, []);
  return { status, active: Boolean(status?.available && enabled) };
}

/** Sección principal de Chat: la conversación ocupa la pantalla completa. */
export function ChatScreen() {
  const { t } = useI18n();
  const ai = useAi();
  return (
    <div className="chat-screen">
      <header className="chat-screen__header">
        <div>
          <h1>{t('chat.title')}</h1>
          <p className="page-header__sub">{t('chat.intro')}</p>
          <p className={`chat-ai${ai.active ? ' chat-ai--on' : ''}`} data-testid="chat-ai">
            <Cpu size={14} aria-hidden="true" />
            {ai.active ? t('chat.ai.on', { provider: t(`assistantSettings.ai.providers.${ai.status!.provider}`), model: ai.status!.model ?? '' }) : t('chat.ai.off')}
          </p>
        </div>
        <Link to="/settings/assistant" className="icon-btn" aria-label={t('assistantSettings.title')}>
          <Settings2 size={22} aria-hidden="true" />
        </Link>
      </header>
      <AssistantChat ai={ai.active} />
    </div>
  );
}

type Busy = 'thinking' | 'applying' | null;

function AssistantChat({ ai }: { ai: boolean }) {
  const { t, locale } = useI18n();
  const { data, ctx } = useApp();
  const sync = useSyncState();
  const toast = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState<ConversationState | null>(null);
  const stateRef = useRef<ConversationState | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [text, setText] = useState('');
  // Borrador desde Hoy o desde "Compartir" (?draft=): se escribe en el campo, nunca se envía solo.
  const [params, setParams] = useSearchParams();
  const draft = params.get('draft');
  const [seenDraft, setSeenDraft] = useState<string | null>(null);
  if (draft !== null && draft !== seenDraft) {
    setSeenDraft(draft);
    setText(draft.slice(0, 500));
  }
  useEffect(() => {
    if (draft === null) return;
    setParams({}, { replace: true });
    inputRef.current?.focus();
  }, [draft, setParams]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const lastInput = useRef<Input | null>(null);

  // Preferencias recordadas (dispositivo): se leen al abrir y se actualizan al guardar una.
  const prefsRef = useRef<Preferences | undefined>(undefined);
  // Plan actual, para que las propuestas avisen antes de superar su límite.
  const planRef = useRef<PlanId | undefined>(undefined);
  const env = useCallback(
    (): ConversationEnv => ({
      prefs: prefsRef.current,
      plan: planRef.current,
      data,
      ctx,
      locale: locale === 'en' ? 'en' : 'es',
      now: Date.now(),
      newId: uuidv7,
      understander: ai ? understander : undefined,
    }),
    [data, ctx, locale, ai],
  );

  /** Guarda y después muestra: lo que se ve ya está persistido, así una recarga no pierde el turno. */
  const commit = useCallback(async (s: ConversationState) => {
    await saveConversation(s);
    stateRef.current = s;
    setState(s);
  }, []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const owner = await conversationOwner();
        prefsRef.current = await loadPreferences(owner);
        planRef.current = await currentPlan();
        const e = { now: Date.now(), newId: uuidv7 };
        const loaded = await loadConversation(owner, e.now);
        let s = loaded ? recoverConversation(loaded, e) : newConversation(owner, e);
        // Resumen del día: una vez al día, si no se desactivó en Ajustes.
        if (await getMeta<boolean>('assistantBrief', true)) s = withDailyBrief(s, env());
        if (!alive) return;
        stateRef.current = s;
        setState(s);
        if (s !== loaded) await saveConversation(s);
      } catch {
        if (alive) setFailure('chat.errors.load');
      }
    })();
    return () => {
      alive = false;
    };
    // Solo al abrir el chat; el resumen usa los datos de ese momento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [state?.messages.length, busy]);

  const execute = async (s: ConversationState, effect: Effect): Promise<void> => {
    if (effect.type === 'navigate') {
      if (effect.agenda) await setMeta('todayView', 'agenda');
      navigate(effect.to);
      return;
    }
    if (effect.type === 'undo') return undo(s, effect.proposalId);
    if (effect.type === 'savePrefs') {
      let ok = true;
      try {
        const next = applyPreference(prefsRef.current ?? (await loadPreferences(s.owner)), effect.change);
        await savePreferences(s.owner, next);
        prefsRef.current = next;
      } catch {
        ok = false;
      }
      await commit(recordPrefs(s, effect.change, ok, env()));
      return;
    }
    setBusy('applying');
    const e = env();
    let next: ConversationState;
    let goTo: string | undefined;
    try {
      const pushActive = sync.signedIn && (await hasPushSubscription());
      const { result: r, writes } = await recordWrites(() => applyAll(effect.items, { data, ctx, pushActive }));
      if (r.undo) undoRegistry.set(effect.proposalId, r.undo);
      next = recordResult(
        s,
        effect.proposalId,
        {
          ok: true,
          message: r.message,
          params: r.params,
          undoable: Boolean(r.undo),
          writes,
        },
        e,
      );
      goTo = r.navigate;
    } catch (err) {
      const error: TextRef = err instanceof PlanLimitError ? tr('editor.planLimit', { limit: err.limit }) : tr('chat.r.applyFailed');
      next = recordResult(s, effect.proposalId, { ok: false, error, retryable: !(err instanceof PlanLimitError) }, e);
    }
    await commit(next);
    if (goTo) navigate(goTo);
  };

  const send = async (input: Input) => {
    const current = stateRef.current;
    if (!current || busy) return;
    lastInput.current = input;
    setFailure(null);
    setBusy('thinking');
    try {
      const r = await step(current, input, env());
      await commit(r.state);
      if (r.effect) await execute(r.state, r.effect);
    } catch {
      setFailure('chat.errors.step');
    } finally {
      setBusy(null);
    }
  };

  const submit = () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    void send({ type: 'text', text: value });
  };

  const undo = async (s: ConversationState, proposalId: string) => {
    const rec = s.proposals.find((p) => p.id === proposalId);
    const fn = undoRegistry.get(proposalId);
    if (!rec || (!rec.undo?.length && !fn)) {
      await commit(recordUndo(s, proposalId, false, env()));
      return;
    }
    setBusy('applying');
    let ok = true;
    let conflicts = 0;
    try {
      if (rec.undo?.length) conflicts = (await revertWrites(rec.undo as WriteTrace[])).conflicts;
      else await fn!();
      undoRegistry.delete(proposalId);
    } catch {
      ok = false;
    }
    await commit(recordUndo(s, proposalId, ok, env(), conflicts));
    setBusy(null);
    toast.show(t(!ok ? 'chat.r.undoFailed' : conflicts ? 'chat.undo.partial' : 'assistant.undone', { count: conflicts }));
  };

  const reset = async () => {
    const s = stateRef.current;
    if (!s) return;
    for (const p of s.proposals) undoRegistry.delete(p.id);
    await clearConversation(s.owner);
    await commit(newConversation(s.owner, { now: Date.now(), newId: uuidv7 }));
    inputRef.current?.focus();
  };

  const editInForm = (rec: ProposalRecord) => {
    const p = rec.items[0];
    if (!p || rec.items.length !== 1 || (p.kind !== 'create_habit' && p.kind !== 'create_task')) return;
    const draft =
      p.kind === 'create_habit'
        ? {
            ...emptyDraft(ctx.today, p.habitKind),
            name: p.name,
            goalType: p.goalType,
            target: p.target,
            unit: p.unit,
            schedule: p.schedule,
          }
        : {
            ...emptyDraft(ctx.today, 'task'),
            name: p.name,
            dueDate: p.dueDate,
            dueTime: p.dueTime,
          };
    navigate('/habits/new', { state: { draft } });
  };

  if (!state) {
    return failure ? (
      <p className="field__error" role="alert">
        {t(failure)}
      </p>
    ) : (
      <p className="muted small" role="status">
        {t('chat.loading')}
      </p>
    );
  }

  const pending = pendingProposal(state);
  const lastAssistant = [...state.messages].reverse().find((m) => m.role === 'assistant');
  // Cada propuesta se muestra completa solo en el último mensaje que la cita.
  const lastRef = new Map<string, string>();
  for (const m of state.messages) for (const p of m.parts) if (p.t === 'proposal') lastRef.set(p.id, m.id);

  return (
    <div className="chat">
      <div className="chat__toolbar">
        <span className="muted xsmall">{t(ai ? 'chat.privacyAi' : 'chat.privacy')}</span>
        {state.messages.length > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void reset()} disabled={Boolean(busy)}>
            <RotateCcw size={16} aria-hidden="true" /> {t('chat.newConversation')}
          </button>
        )}
      </div>

      <div className="chat__log" role="log" aria-label={t('chat.history')} aria-live="polite" aria-relevant="additions">
        {state.messages.map((m) => (
          <MessageView
            key={m.id}
            message={m}
            state={state}
            showChoices={m === lastAssistant && (!state.question || state.question.messageId === m.id)}
            fullProposal={(id) => lastRef.get(id) === m.id}
            busy={Boolean(busy)}
            onChoice={(choiceId) => void send({ type: 'choice', messageId: m.id, choiceId })}
            onConfirm={(id) => void send({ type: 'confirm', proposalId: id })}
            onCancel={(id) => void send({ type: 'cancel', proposalId: id })}
            onChange={() => {
              inputRef.current?.focus();
              setText('');
            }}
            onEditInForm={editInForm}
            onUndo={(id) => void send({ type: 'undo', proposalId: id })}
          />
        ))}
        {!state.messages.some((m) => m.role === 'user') && (
          <div className="chat__empty">
            <p className="chat__empty-title">{t('chat.emptyTitle')}</p>
            <p className="muted small">{t('chat.emptyBody')}</p>
            <div className="chat__choices" role="group" aria-label={t('assistant.examplesTitle')}>
              {EXAMPLES.map((k) => (
                <button key={k} type="button" className="chip-toggle" onClick={() => void send({ type: 'text', text: t(k) })}>
                  {t(k)}
                </button>
              ))}
            </div>
          </div>
        )}
        {busy && (
          <div className="chat__msg chat__msg--assistant">
            <span className="chat__typing" role="status">
              <span className="chat__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              {t(busy === 'applying' ? 'chat.applying' : 'chat.thinking')}
            </span>
          </div>
        )}
        {failure && (
          <div className="chat__msg chat__msg--assistant">
            <div className="chat__bubble chat__bubble--error" role="alert">
              <p>{t(failure)}</p>
              {/* Un paso fallido no cambia el estado: reenviar la misma entrada es seguro. */}
              <button type="button" className="btn btn--outline btn--sm" onClick={() => lastInput.current && void send(lastInput.current)}>
                {t('errors.retry')}
              </button>
            </div>
          </div>
        )}
        <div ref={endRef} className="chat__end" />
      </div>

      <div className="chat__dock">
        <nav className="chat__shortcuts" aria-label={t('chat.shortcuts.label')}>
          {SHORTCUTS.map(({ key, Icon, to }) => (
            <Link key={key} to={to} className="chat__shortcut">
              <Icon size={16} aria-hidden="true" /> {t(key)}
            </Link>
          ))}
        </nav>

        <form
          className="chat__composer"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label htmlFor="assistant-input" className="visually-hidden">
            {t('assistant.inputLabel')}
          </label>
          <textarea
            id="assistant-input"
            ref={inputRef}
            className="input chat__input"
            rows={1}
            autoComplete="off"
            enterKeyHint="send"
            maxLength={500}
            placeholder={pending ? t('chat.placeholderPending') : t('chat.placeholder')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <DictationButton
            lang={locale === 'en' ? 'en-US' : 'es-ES'}
            base={text}
            onText={setText}
            disabled={Boolean(busy)}
            labels={{ start: t('chat.voice.start'), stop: t('chat.voice.stop'), listening: t('chat.voice.listening'), privacy: t('chat.voice.privacy'), error: t('chat.voice.error') }}
          />
          <button type="submit" className="btn btn--primary chat__send" disabled={!text.trim() || Boolean(busy)} aria-label={t('chat.send')}>
            <Send size={18} aria-hidden="true" />
          </button>
        </form>
      </div>
    </div>
  );
}

interface MessageProps {
  message: Message;
  state: ConversationState;
  showChoices: boolean;
  fullProposal: (id: string) => boolean;
  busy: boolean;
  onChoice: (id: string) => void;
  onConfirm: (id: string) => void;
  onCancel: (id: string) => void;
  onChange: () => void;
  onEditInForm: (rec: ProposalRecord) => void;
  onUndo: (id: string) => void;
}

function MessageView({ message: m, state, showChoices, fullProposal, busy, onChoice, onConfirm, onCancel, onChange, onEditInForm, onUndo }: MessageProps) {
  const { t } = useI18n();
  if (m.role === 'user') {
    return (
      <div className="chat__msg chat__msg--user">
        <div className="chat__bubble chat__bubble--user">
          <span className="visually-hidden">{t('chat.you')}: </span>
          {m.text}
        </div>
      </div>
    );
  }
  // Las listas "plain" son la versión en texto de una tarjeta: con tarjeta no se repiten.
  const hasCard = m.parts.some((p) => p.t === 'agenda' || p.t === 'habits' || p.t === 'tasks');
  const tone = m.kind === 'error' ? ' chat__bubble--error' : m.kind === 'result' ? ' chat__bubble--result' : m.kind === 'question' ? ' chat__bubble--question' : '';
  return (
    <div className="chat__msg chat__msg--assistant">
      <div className={`chat__bubble${tone}`}>
        <span className="visually-hidden">{t('assistant.title')}: </span>
        {m.kind === 'result' && <Check size={16} className="chat__icon" aria-hidden="true" />}
        {m.parts.filter((p) => !(p.t === 'list' && p.plain && hasCard)).map((p, i) => (
          <PartView
            key={i}
            part={p}
            state={state}
            full={p.t === 'proposal' && fullProposal(p.id)}
            busy={busy}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onChange={onChange}
            onEditInForm={onEditInForm}
            onUndo={onUndo}
          />
        ))}
      </div>
      {showChoices && m.choices.length > 0 && (
        <div className="chat__choices" role="group" aria-label={t(m.kind === 'question' ? 'chat.options' : 'chat.suggestions')}>
          {m.choices.map((c) => (
            <button key={c.id} type="button" className="chip-toggle" disabled={busy} onClick={() => onChoice(c.id)}>
              {t(c.label.key, c.label.params)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PartView({
  part,
  state,
  full,
  busy,
  onConfirm,
  onCancel,
  onChange,
  onEditInForm,
  onUndo,
}: {
  part: Part;
  state: ConversationState;
  full: boolean;
  busy: boolean;
} & Pick<MessageProps, 'onConfirm' | 'onCancel' | 'onChange' | 'onEditInForm' | 'onUndo'>) {
  const { t } = useI18n();
  const { data } = useApp();
  switch (part.t) {
    case 'text':
      return <p className="chat__text">{t(part.text.key, part.text.params)}</p>;
    case 'caption':
      return <p className="chat__caption">{t(part.text.key, part.text.params)}</p>;
    case 'list':
      return (
        <div className="chat__list">
          {part.title && <p className="chat__list-title">{t(part.title.key, part.title.params)}</p>}
          <ul>
            {part.items.map((it, i) => (
              <li key={i}>{t(it.key, it.params)}</li>
            ))}
          </ul>
        </div>
      );
    case 'notes': {
      const notes = part.ids.map((id) => data.notes.find((n) => n.id === id && !n.deletedAt)).filter((n) => n !== undefined);
      if (!notes.length) return <p className="chat__caption">{t('chat.a.notesGone')}</p>;
      return (
        <ul className="chat__list">
          {notes.map((n) => (
            <li key={n.id}>
              <strong>{n.title}</strong> <span className="muted xsmall">· {t(`brain.kinds.${n.kind}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'journal': {
      const entries = part.ids.map((id) => data.journal.find((j) => j.id === id && !j.deletedAt)).filter((j) => j !== undefined);
      if (!entries.length) return <p className="chat__caption">{t('chat.a.journalGone')}</p>;
      return (
        <>
          {entries.map((j) => (
            <blockquote key={j.id} className="chat__quote">
              {j.text.length > 280 ? `${j.text.slice(0, 280)}…` : j.text}
            </blockquote>
          ))}
        </>
      );
    }
    case 'agenda':
      return <AgendaCard from={part.from} to={part.to} />;
    case 'habits':
      return <HabitsCard ids={part.ids} />;
    case 'tasks':
      return <TasksCard ids={part.ids} />;
    case 'proposal': {
      const rec = state.proposals.find((p) => p.id === part.id);
      if (!rec) return <p className="chat__caption">{t('chat.p.gone')}</p>;
      if (!full) return <p className="chat__caption">{t('chat.p.seeBelow')}</p>;
      return (
        <ProposalCard
          rec={rec}
          active={state.activeProposalId === rec.id}
          busy={busy}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onChange={onChange}
          onEditInForm={onEditInForm}
          onUndo={onUndo}
        />
      );
    }
  }
}

function ProposalCard({
  rec,
  active,
  busy,
  onConfirm,
  onCancel,
  onChange,
  onEditInForm,
  onUndo,
}: { rec: ProposalRecord; active: boolean; busy: boolean } & Pick<MessageProps, 'onConfirm' | 'onCancel' | 'onChange' | 'onEditInForm' | 'onUndo'>) {
  const i18n = useI18n();
  const { t } = i18n;
  const { data } = useApp();
  const plain = plainI18n(i18n.locale === 'en' ? 'en' : 'es');
  const destructive = rec.items.find((p) => DESTRUCTIVE.has(p.kind));
  const pending = rec.status === 'pending' && active;
  const single = rec.items.length === 1 ? rec.items[0]! : null;
  const canForm = single && (single.kind === 'create_habit' || single.kind === 'create_task');
  return (
    <section className={`card stack chat__proposal${destructive ? ' chat__proposal--danger' : ''}`} aria-label={t('assistant.proposalTitle')}>
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <strong>{single ? t(`assistant.kinds.${single.kind}`) : t('chat.p.planTitle', { count: rec.items.length })}</strong>
        <span className={`chat__status chat__status--${rec.status}`}>
          {t(`chat.status.proposal.${rec.status}`)}
          {rec.revision > 1 ? ` · ${t('chat.p.revision', { revision: rec.revision })}` : ''}
        </span>
      </div>
      {rec.items.map((p, idx) => {
        const rows = describeProposal(p, plain, data);
        if (!rows.length) return null;
        return (
          <dl key={idx} className="chat__fields">
            {!single && <dt className="chat__item-title">{t(`assistant.kinds.${p.kind}`)}</dt>}
            {rows.map(([k, v]) => (
              <div key={k} className="chat__field">
                <dt className="muted small">{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        );
      })}
      {rec.explain.length > 0 && (
        <details className="chat__explain" open={pending}>
          <summary>{t('chat.p.why')}</summary>
          <ul>
            {rec.explain.map((x, i) => (
              <li key={i}>{t(x.key, x.params)}</li>
            ))}
          </ul>
        </details>
      )}
      {rec.warnings.length > 0 && (
        <ul className="chat__warnings">
          {rec.warnings.map((x, i) => (
            <li key={i}>{t(x.key, x.params)}</li>
          ))}
        </ul>
      )}
      {pending && destructive && (
        <p className="field__error row" role="alert">
          <AlertTriangle size={18} aria-hidden="true" /> {t('chat.p.destructiveNote')}
        </p>
      )}
      {pending && (
        <div className="chat__actions">
          <button type="button" className={destructive ? 'btn btn--danger' : 'btn btn--primary'} disabled={busy} onClick={() => onConfirm(rec.id)}>
            {destructive ? t(`assistant.confirm.${destructive.kind}`) : t('assistant.confirm.default')}
          </button>
          <button type="button" className="btn btn--outline" disabled={busy} onClick={onChange}>
            {t('chat.p.change')}
          </button>
          {canForm && (
            <button type="button" className="btn btn--outline" disabled={busy} onClick={() => onEditInForm(rec)}>
              {t('assistant.editInForm')}
            </button>
          )}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onCancel(rec.id)}>
            {t('common.cancel')}
          </button>
        </div>
      )}
      {rec.status === 'applied' && (Boolean(rec.undo?.length) || undoRegistry.has(rec.id)) && (
        <div className="chat__actions">
          <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => onUndo(rec.id)}>
            {t('common.undo')}
          </button>
        </div>
      )}
    </section>
  );
}
